import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./ChatWithCenter.module.scss";
import { useContext, useEffect, useRef, useState } from "react";
import Chat from "@medoma/components/Chat/Chat";
import { Loading } from "@medoma/components/Loading/Loading";
import ChatContext from "@medoma/chat/ChatContext";
import { formatTimestamp, makeMessages } from "@medoma/chat/useMatrixEvents";
import { useSendReadReceiptOnSeen } from "@medoma/chat/useSendReadReceipt";
import { useMatrixRoom } from "@medoma/chat/useMatrixRoom";
import { sendTextMessage } from "@medoma/chat/matrixClient";
import { useChatScroll } from "@medoma/chat/useChatScroll";
import { SyncState } from "matrix-js-sdk";
import { useMatrixConnectionStatus } from "@medoma/chat/useMatrixConnectionStatus";
import { useMatrixTimelineEvents } from "@medoma/chat/useMatrixEvents";
import {
  lastEventReadByUserOtherThanUserId,
  useLastReadEventPerUser,
} from "@medoma/chat/useLastReadEvent";
import { getHistory } from "@medoma/chat/getHistory";
import { Heading } from "@medoma/components/Heading/Heading";
import { medicalTitle } from "@medoma/models/shifts";
import { useOtherUserIsTyping } from "@medoma/chat/useOtherUserIsTyping";
import { useSendTypingNotification } from "@medoma/chat/useSendTypingNotification";
import { getEmployeeRoomAlias } from "@medoma/chat/roomAlias";
import { useUserProfile } from "@/api/Users";
import { CHAT_ENVIRONMENT } from "@/Utils/EnvUtils";
import { useParams } from "react-router";
import { siteIdSchema } from "@medoma/models/sites";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { sitesQueryOptions } from "@/api/Patients";

const ChatWithCenter = () => {
  const { t } = useLingui();
  const [pendingMessage, setPendingMessage] = useState("");
  const { site } = z.object({ site: siteIdSchema }).parse(useParams());
  const { employeeNumber, organization } = useUserProfile();
  const { matrixClient } = useContext(ChatContext);
  const { otherUserIsTyping: centerIsTyping } = useOtherUserIsTyping({
    matrixClient,
  });
  const chatConnection = useMatrixConnectionStatus(matrixClient);
  const roomAlias = getEmployeeRoomAlias({
    employeeId: employeeNumber,
    env: CHAT_ENVIRONMENT,
    matrixClient,
    organization,
    site,
  });
  const { roomId, getRoom } = useMatrixRoom(matrixClient, roomAlias);
  const { sendNoLongerTypingNotification, updateTypingState } =
    useSendTypingNotification({
      matrixClient,
      roomId,
    });

  // Send no longer typing notification when leaving chat
  useEffect(() => {
    return () => {
      sendNoLongerTypingNotification();
    };
  }, [sendNoLongerTypingNotification]);
  const userId = matrixClient?.getUserId() ?? undefined;

  const timelineEvents = useMatrixTimelineEvents(matrixClient, roomId);

  const lastReadEventBySomeNonGoEmployeeUser =
    lastEventReadByUserOtherThanUserId(
      useLastReadEventPerUser({ getRoom }),
      userId,
    );

  const shouldRenderInfoPanel =
    centerIsTyping || chatConnection === SyncState.Error;

  const messages = userId ? makeMessages(timelineEvents, userId) : [];

  const latestMessage = messages[messages.length - 1]?.id ?? null;

  useSendReadReceiptOnSeen(matrixClient, timelineEvents, userId);

  const messagePanelRef = useRef<HTMLDivElement>(null);
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const { scrollMessagePanelToBottom } = useChatScroll({
    messagePanelRef,
    inputTextAreaRef,
    messages,
  });

  const sendMessage = () => {
    if (!matrixClient || !roomId) {
      return;
    }
    sendTextMessage(matrixClient, roomId, pendingMessage, () =>
      setPendingMessage(""),
    );
    sendNoLongerTypingNotification();
  };

  const room = getRoom();

  const { data: sites, isPending: isSitesPending } =
    useQuery(sitesQueryOptions);

  if (!matrixClient || !room || isSitesPending) {
    return <Loading message={t`Laddar chatt`} padding={24} />;
  }

  // For users with only one site, it makes more sense to display "Center" instead of the site name
  const showSiteDisplayName = sites && sites.length > 1;
  const siteDisplayName = showSiteDisplayName
    ? (sites?.find(({ id }) => id === site)?.displayName ?? t`Center`)
    : t`Center`;

  // Clear app badge when entering chat (is set in service-worker.js)
  // Not available in Safari browser unless the website is added to home screen
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/clearAppBadge
  if (navigator.clearAppBadge) {
    void navigator.clearAppBadge();
  }

  return (
    <article className={styles.chatContainer}>
      <div className={styles.chatWrapper}>
        <Chat>
          <Chat.HeaderPanel>
            <Heading level="h1" size="h3">
              <Trans>Chatt med {siteDisplayName}</Trans>
            </Heading>
          </Chat.HeaderPanel>
          <Chat.MessagePanel
            getHistory={() => getHistory(matrixClient, room)}
            ref={messagePanelRef}
          >
            {messages.map((message) =>
              message.sender.isMe ? (
                <Chat.MessageGroup
                  key={message.id}
                  sender="me"
                  status={message.status}
                  timestamp={formatTimestamp(message.epochTimestamp)}
                  readIndicator={
                    message.id === lastReadEventBySomeNonGoEmployeeUser?.eventId
                      ? lastReadEventBySomeNonGoEmployeeUser.timestamp
                      : undefined
                  }
                  isLatestMessage={latestMessage === message.id}
                >
                  <Chat.Message
                    type="sent-by-me"
                    message={message.text}
                    enableClickableLinks={true}
                  />
                </Chat.MessageGroup>
              ) : (
                <Chat.MessageGroup
                  key={message.id}
                  sender="other-person"
                  senderName={message.sender.name ?? t`Center`}
                  senderTitle={
                    medicalTitle(message.sender.title, {
                      format: "short",
                    }) ?? null
                  }
                  senderPhotoURL={message.sender.imageURL ?? null}
                  status={message.status}
                  timestamp={formatTimestamp(message.epochTimestamp)}
                >
                  <Chat.Message
                    type="received"
                    message={message.text}
                    enableClickableLinks={true}
                  />
                </Chat.MessageGroup>
              ),
            )}
          </Chat.MessagePanel>
          {shouldRenderInfoPanel ? (
            <Chat.InfoPanel
              type={chatConnection === SyncState.Error ? "error" : "info"}
            >
              {chatConnection === SyncState.Error ? (
                t`Chatten fungerar inte just nu`
              ) : (
                <Chat.IsTyping text={t`Center skriver`} />
              )}
            </Chat.InfoPanel>
          ) : (
            <></>
          )}
          <Chat.InputPanel>
            <Chat.InputTextArea
              inputTextAreaRef={inputTextAreaRef}
              value={pendingMessage}
              onChange={(event) => {
                setPendingMessage(event.currentTarget.value);
                updateTypingState({ event, pendingMessage });
              }}
              onFocus={scrollMessagePanelToBottom}
            />
            <Chat.SendMessageButton
              aria-label={t`Skicka`}
              disabled={pendingMessage.trim() === ""}
              onClick={sendMessage}
            />
          </Chat.InputPanel>
        </Chat>
      </div>
    </article>
  );
};

export { ChatWithCenter };
